import { Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { LoadingSpinner } from '@hanwen/ui-components';

// Layout components
import ResponsiveLayout from './components/layout/ResponsiveLayout';
import PublicLayout from './components/public/PublicLayout';
// Auth context/hook
import { useAuth } from './hooks/useAuth.ts';
import React from 'react';

// Public landing pages (lazy loaded)
const HomePage = React.lazy(() => import('./pages/public/HomePage'));
const TimetablePage = React.lazy(() => import('./pages/public/TimetablePage'));
const ProgramMandarinPage = React.lazy(() => import('./pages/public/ProgramMandarinPage'));
const ProgramEventsPage = React.lazy(() => import('./pages/public/ProgramEventsPage'));
const FaqPage = React.lazy(() => import('./pages/public/FaqPage'));

// Pages (lazy loaded)
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const ForgotPasswordPage = React.lazy(() => import('./pages/ForgotPasswordPage'));
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const StudentsPage = React.lazy(() => import('./pages/StudentsPage'));
const ClassesPage = React.lazy(() => import('./pages/ClassesPage'));
const TeachersPage = React.lazy(() => import('./pages/TeachersPage'));
const CampusesPage = React.lazy(() => import('./pages/CampusesPage'));
const AbsencePage = React.lazy(() => import('./pages/AbsencePage'));
const MakeupPage = React.lazy(() => import('./pages/MakeupPage'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));
const AuditLogPage = React.lazy(() => import('./pages/AuditLogPage'));
const DataManagementPage = React.lazy(() => import('./pages/DataManagementPage'));

// Parent pages
const ParentLayout = React.lazy(() => import('./pages/parent/ParentLayout'));
const ParentHomePage = React.lazy(() => import('./pages/parent/ParentHomePage'));
const ParentProfilePage = React.lazy(() => import('./pages/parent/ParentProfilePage'));
const GuidePage = React.lazy(() => import('./pages/GuidePage'));

// 公开页面路径前缀（其余路径默认进入应用模式）
const PUBLIC_PAGE_PATHS = ['/', '/timetable', '/programs', '/faq'];

function isPublicPagePath(pathname: string): boolean {
  return PUBLIC_PAGE_PATHS.some((p) => pathname === p || pathname.startsWith(p + '/'));
}

// 决定当前页面渲染公开站还是应用
function isPublicDomain(pathname: string): boolean {
  const searchParams = new URLSearchParams(window.location.search);
  const hostname = window.location.hostname;

  // ?app=1 强制应用模式；?app=0 强制公开页面
  const appParam = searchParams.get('app');
  if (appParam === '1') return false;
  if (appParam === '0') return true;

  // 生产 app. 子域名整站为应用模式（公开站由主域名承载）
  if (hostname.startsWith('app.')) return false;

  // 其余环境（本地、staging、生产主域名）按路径决定：公开路径走公开站，其他走应用
  return isPublicPagePath(pathname);
}

// 公开页面路由树
const PublicRouteTree: React.FC = () => {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        <Route element={<PublicLayout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/timetable" element={<TimetablePage />} />
          <Route path="/timetable/:term" element={<TimetablePage />} />
          <Route path="/programs/mandarin" element={<ProgramMandarinPage />} />
          <Route path="/programs/events" element={<ProgramEventsPage />} />
          <Route path="/faq" element={<FaqPage />} />
          <Route path="*" element={<HomePage />} />
        </Route>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/guide" element={<GuidePage />} />
      </Routes>
    </Suspense>
  );
};

// 家长路由守卫组件
const ParentRouteGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  
  // 检查是否为家长角色
  if (user?.role !== 'parent') {
    // 非家长用户重定向到 dashboard
    return <Navigate to="/dashboard" replace />;
  }
  
  return <>{children}</>;
};

// 管理员/教师路由守卫组件
const AdminRouteGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  // 检查是否为 admin 或 teacher 角色
  const allowedRoles = ['admin', 'teacher'];
  if (!allowedRoles.includes(user?.role || '')) {
    // 非管理用户重定向到家长首页
    return <Navigate to="/parent" replace />;
  }

  return <>{children}</>;
};
const RoleBasedHome: React.FC = () => {
  const { user } = useAuth();
  
  // 家长跳转到 /parent，其他跳转到 /dashboard
  if (user?.role === 'parent') {
    return <Navigate to="/parent" replace />;
  }
  return <Navigate to="/dashboard" replace />;
};

// 生成App实例ID
let appCounter = 0;

// 全局路由历史，防止路由循环
let lastRoutePath = '';

function App() {
  // 按路径决定渲染公开站还是应用（两种页面在所有环境都可直达）
  const location = useLocation();
  if (isPublicDomain(location.pathname)) {
    return <PublicRouteTree />;
  }
  return <AppContent />;
}

// 应用模式主体（仅在应用模式下挂载，公开页不触发 auth 逻辑）
function AppContent() {
  const location = useLocation();

  const instanceId = `App-${++appCounter}`;

  // 追踪路由变化
  React.useEffect(() => {
    if (lastRoutePath !== location.pathname) {
      console.log(`📍 [${instanceId}] 路由变化: ${lastRoutePath} -> ${location.pathname}`);
      lastRoutePath = location.pathname;
    }
  }, [location.pathname, instanceId]);
  
  // 组件生命周期日志
  React.useEffect(() => {
    console.log(`🚀 [${instanceId}] App组件挂载`);
    return () => {
      console.log(`🗑️ [${instanceId}] App组件卸载`);
    };
  }, [instanceId]);
  
  const { isAuthenticated, isLoading } = useAuth();

  // 渲染计数器
  const renderCount = React.useRef(0);
  renderCount.current++;
  console.log(`🏠 [${instanceId}] App渲染 #${renderCount.current} - isAuthenticated:`, isAuthenticated, 'isLoading:', isLoading, '路径:', location.pathname);
  
  if (isLoading) {
    console.log(`⏳ [${instanceId}] 加载中，显示加载动画`);
    return <LoadingSpinner />;
  }

  // 简单的防闪烁机制：如果已经在保护路径上，保持认证状态一小段时间
  const [stableIsAuthenticated, setStableIsAuthenticated] = React.useState(isAuthenticated);
  const debounceTimeout = React.useRef<NodeJS.Timeout>();
  
  React.useEffect(() => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    
    // 认证状态从 false 变为 true：立即更新（登录成功）
    if (!stableIsAuthenticated && isAuthenticated) {
      console.log(`⚡ [${instanceId}] 登录成功，立即更新认证状态`);
      setStableIsAuthenticated(true);
    }
    // 认证状态从 true 变为 false：短暂延迟（防止路由切换时的闪烁）
    else if (stableIsAuthenticated && !isAuthenticated) {
      const isOnProtectedRoute = ['/dashboard', '/students', '/classes', '/absences', '/makeup', '/profile']
        .some(route => location.pathname.startsWith(route));
      
      if (isOnProtectedRoute) {
        console.log(`⏳ [${instanceId}] 在保护路径上检测到认证状态变为false，延迟1秒检查`);
        debounceTimeout.current = setTimeout(() => {
          console.log(`⏰ [${instanceId}] 延迟后认证状态仍然为false，更新状态`);
          setStableIsAuthenticated(false);
        }, 1000);
      } else {
        console.log(`🔄 [${instanceId}] 不在保护路径上，立即更新认证状态`);
        setStableIsAuthenticated(false);
      }
    }
    // 状态同步
    else if (stableIsAuthenticated !== isAuthenticated) {
      console.log(`🔄 [${instanceId}] 同步认证状态: ${stableIsAuthenticated} -> ${isAuthenticated}`);
      setStableIsAuthenticated(isAuthenticated);
    }
    
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [isAuthenticated, stableIsAuthenticated, location.pathname, instanceId]);

  // Public routes - 使用稳定的认证状态
  if (!stableIsAuthenticated) {
    console.log(`🔓 [${instanceId}] 显示公开路由（未认证状态），路径: ${location.pathname}`);
    
    // 如果是/login或/guide路径，直接显示对应页面
    if (location.pathname === '/login' || location.pathname === '/guide') {
      return (
        <Suspense fallback={<LoadingSpinner />}>
          <Routes location={location}>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/guide" element={<GuidePage />} />
            <Route path="*" element={<LoginPage />} />
          </Routes>
        </Suspense>
      );
    }

    // 其他公开路径
    return (
      <Suspense fallback={<LoadingSpinner />}>
        <Routes location={location}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/guide" element={<GuidePage />} />
          <Route path="*" element={<Navigate to="/login" replace state={{ from: location.pathname }} />} />
        </Routes>
      </Suspense>
    );
  }

  // Authenticated routes with responsive layout
  console.log(`🔐 [${instanceId}] 显示认证路由（已认证状态），路径: ${location.pathname}`);
  return (
    <ResponsiveLayout>
      <Suspense fallback={<LoadingSpinner />}>
        <Routes location={location}>
          <Route path="/" element={<RoleBasedHome />} />
          <Route path="/dashboard" element={<AdminRouteGuard><DashboardPage /></AdminRouteGuard>} />
          <Route path="/students" element={<AdminRouteGuard><StudentsPage /></AdminRouteGuard>} />
          <Route path="/classes" element={<AdminRouteGuard><ClassesPage /></AdminRouteGuard>} />
          <Route path="/classes/:id" element={<AdminRouteGuard><ClassesPage /></AdminRouteGuard>} />
          <Route path="/teachers" element={<AdminRouteGuard><TeachersPage /></AdminRouteGuard>} />
          <Route path="/campuses" element={<AdminRouteGuard><CampusesPage /></AdminRouteGuard>} />
          <Route path="/absences" element={<AdminRouteGuard><AbsencePage /></AdminRouteGuard>} />
          <Route path="/makeup" element={<AdminRouteGuard><MakeupPage /></AdminRouteGuard>} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/audit-logs" element={<AdminRouteGuard><AuditLogPage /></AdminRouteGuard>} />
          <Route path="/data-management" element={<AdminRouteGuard><DataManagementPage /></AdminRouteGuard>} />
          <Route path="/guide" element={<GuidePage />} />

          {/* 家长端路由 - 需要家长角色 */}
          <Route
            path="/parent"
            element={
              <ParentRouteGuard>
                <ParentLayout />
              </ParentRouteGuard>
            }
          >
            <Route index element={<ParentHomePage />} />
            <Route path="profile" element={<ParentProfilePage />} />
          </Route>
          
          <Route path="/login" element={<RoleBasedHome />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </ResponsiveLayout>
  );
}

export default App;
